import { Input, InputNumberProps, InputProps } from 'antd';
import { KeyboardEvent, useEffect, useMemo, useState } from 'react';
import { decimalSeparatorFor, parseMoneyInput, useInputLanguage } from './MoneyInput';

type Parser = NonNullable<InputNumberProps<number>['parser']>;
type Formatter = NonNullable<InputNumberProps<number>['formatter']>;

// antd's default parser deletes commas, so a Greek `12,5` became 125.
// `grouping: false` reads any lone separator as the decimal point (values that never reach a thousand).
export const useDecimalInputProps = ({ precision, grouping = false }: { precision: number; grouping?: boolean }) => {
  const language = useInputLanguage();
  return useMemo(() => {
    const separator = decimalSeparatorFor(language);
    const factor = 10 ** precision;
    // rc-input-number parses the returned decimal string itself ('' clears the field).
    const parser = ((raw: string | undefined) => parseMoneyInput(raw, language, { grouping })) as unknown as Parser;
    // Up to `precision` decimals without trailing zeros, in the locale's separator.
    const formatter: Formatter = (value, info) => {
      if (info.userTyping) return info.input;
      if (value === undefined || value === null || String(value) === '') return '';
      const number = Number(value);
      if (Number.isNaN(number)) return info.input;
      return String(Math.round(number * factor) / factor).replace('.', separator);
    };
    return { precision, parser, formatter } satisfies InputNumberProps<number>;
  }, [language, precision, grouping]);
};

// A typed comma or dot in a count is refused at the keyboard, and a pasted one leaves the value
// unchanged, instead of antd's default parser turning `1,5` into 15.
const INTEGER_TEXT = /^\s*-?\d*\s*$/;
export const integerInputProps = {
  precision: 0,
  parser: ((raw: string | undefined) =>
    INTEGER_TEXT.test(raw ?? '') ? (raw ?? '').trim() : 'NaN') as unknown as Parser,
  onKeyDown: (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === ',' || event.key === '.') event.preventDefault();
  },
} satisfies InputNumberProps<number>;

/** `null` when empty; `NaN` when it is not a number or has more than `precision` decimals. */
export const parseDecimalInput = (raw: string, language: string, precision: number): number | null => {
  const text = raw.trim();
  if (text === '') return null;
  if (!/^-?\d*[.,]?\d*$/.test(text) || !/\d/.test(text)) return NaN;
  const normalised = parseMoneyInput(text, language, { grouping: false });
  const fraction = normalised.split('.')[1] ?? '';
  return fraction.length > precision ? NaN : Number(normalised);
};

export const formatDecimalInput = (value: number | null | undefined, language: string) =>
  value === null || value === undefined || Number.isNaN(value)
    ? ''
    : String(value).replace('.', decimalSeparatorFor(language));

type DecimalInputProps = Omit<InputProps, 'value' | 'onChange' | 'type'> & {
  value: number | null | undefined;
  precision: number;
  /** Called on every keystroke; `NaN` for an entry that is not a valid number, so the caller can refuse it. */
  onChange?: (value: number | null) => void;
};

/** A text box for a decimal in the UI language's separator; keeps what was typed while it is invalid. */
const DecimalInput = ({ value, precision, onChange, ...rest }: DecimalInputProps) => {
  const language = useInputLanguage();
  const [draft, setDraft] = useState(() => formatDecimalInput(value, language));

  useEffect(() => {
    setDraft((current) =>
      Object.is(parseDecimalInput(current, language, precision), value ?? null)
        ? current
        : formatDecimalInput(value, language)
    );
  }, [value, language, precision]);

  return (
    <Input
      inputMode={precision > 0 ? 'decimal' : 'numeric'}
      {...rest}
      value={draft}
      onChange={(event) => {
        setDraft(event.target.value);
        onChange?.(parseDecimalInput(event.target.value, language, precision));
      }}
    />
  );
};

export default DecimalInput;
